/**
 * `/reports` — WP-K8, Task 48; the lifecycle wired in Task U19.
 *
 * ── The list is the company's export queue, and no longer this tab's memory ─
 *
 * `GET /api/v1/reports` exists (`listReports`, `src/modules/reports/routes.ts:262`,
 * `openapi.yaml` `operationId: listReports`, G26 amendment A20). Until it did, this
 * screen kept a **tab-scoped** list seeded from the `202` bodies, with a caption
 * saying so and an empty state that named the tab rather than the company — because
 * a browser's memory presented as a company's export record would have been a fiction
 * on the one page whose subject *is* a record.
 *
 * That workaround is **gone**, not merely supplemented: the state, the caption and
 * the tab-shaped empty text have all been removed, and `reports.test.tsx` asserts
 * their absence rather than only asserting that the new read works. A screen that
 * kept both would show a report twice — once from the `202` and once from the list —
 * and would keep telling the operator their list was incomplete when it no longer is.
 *
 * A create no longer pushes a row into local state; it **re-reads the list**, which
 * is the same discipline `/incidents` uses after a write. The `202` body is not lost:
 * it is in `wp_reports` before the response is written, so the next read carries it.
 *
 * ── What the list does not check, and what the page says about it ──────────
 *
 * `listReportPage` scopes on `company_id` and nothing else — the frozen
 * `scope_employee_ids` of each row are not compared against the caller's live scope
 * the way `grantDownload`'s `scopeStillCovers` compares them. So the queue a manager
 * at `resolved` scope sees is the **whole company's**, while the files they may
 * actually fetch are a subset of it. That asymmetry is the server's and this unit
 * does not paper over it: the caption says the list is company-wide and that a row
 * shown here can still be refused at download. It is reported as a finding in the
 * unit's report; a narrowing belongs on the endpoint, not in a browser that could
 * only hide rows it had already been sent.
 *
 * ── The schedule form is scope-gated, and absent rather than greyed ────────
 *
 * `API_CONTRACT.md` §7.2 gives `POST /reports` scope `resolved` and
 * `POST /report-schedules` scope **`company`** — stricter on purpose, because a
 * schedule names recipients and somebody at level `self` who could create one could
 * arrange for a colleague list to be mailed a company's numbers on a timer. The three
 * `/report-schedules/{id}` operations are `x-wp-scope: company` too, so
 * `ScheduleManager` is behind the same gate.
 *
 * `SessionUser.scope_level` is the level the **server** resolved and published, read
 * the same way `permissions` is. It is not re-derived from `role`, which would be the
 * second authorisation path `nav/menu.ts`'s header forbids — and it decides only what
 * is *rendered*: the chain runs on every call regardless.
 *
 * A session below company scope gets **no form and no manager**, plus a sentence
 * saying why. Not greyed ones: a form that can only ever produce a `403` is not a
 * form, and greying it would send the operator to ask for a permission that is not
 * the problem.
 */

import { useCallback, useState, type ReactNode } from 'react';

import { useI18n } from '../i18n';
import { reportsListPath } from '../api/paths';
import type { ReportListResponse, ReportSchedule } from '../api/types';
import { useApiQuery } from '../api/useApiQuery';
import { useSession } from '../auth/SessionProvider';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { EmptyState } from '../states/EmptyState';
import { PartialData } from '../states/PartialData';
import { Skeleton } from '../states/Skeleton';
import { StatusNote } from '../states/StatusNote';
import { ScreenHeading } from '../shell/ScreenHeading';
import { AbsenceLegend } from './reports/AbsenceLegend';
import { AbsentEndpoints } from './reports/AbsentEndpoints';
import { CreateReportForm } from './reports/CreateReportForm';
import { ReportRow } from './reports/ReportRow';
import { ScheduleForm } from './reports/ScheduleForm';
import { ScheduleManager } from './reports/ScheduleManager';
import type { Navigate } from '../reports/download';

/**
 * One page of the queue.
 *
 * Written out rather than left to the endpoint's default of 50, the rule
 * `api/paths.ts` already applies to `/attendance`: a page boundary is a fact about
 * the request, and a screen that did not choose its own is showing somebody else's.
 * The `total` the endpoint publishes is what lets `PartialData` say how much of the
 * queue is on screen instead of implying this is all of it.
 */
export const REPORT_PAGE_LIMIT = 50;

export interface ReportsScreenProps {
  /** Injected so a test drives the download navigation without an address bar. */
  readonly navigate?: Navigate;
}

export function ReportsScreen({ navigate }: ReportsScreenProps = {}): ReactNode {
  const { t } = useI18n();
  const { user } = useSession();
  const [listToken, setListToken] = useState(0);
  const [scheduleId, setScheduleId] = useState<string | null>(null);

  const list = useApiQuery<ReportListResponse>(
    reportsListPath({ limit: REPORT_PAGE_LIMIT }),
    listToken,
  );

  const onCreated = useCallback(() => {
    // A `202` means the row is already in `wp_reports` — the route writes it before
    // it answers — so the queue is re-read rather than having the body spliced into a
    // local array. One writer for the list, and it is the server.
    setListToken((value) => value + 1);
  }, []);

  const onScheduleCreated = useCallback((schedule: ReportSchedule) => {
    setScheduleId(schedule.id);
  }, []);

  const companyScope = user?.scope_level === 'company';
  const rows = list.data?.data ?? [];
  const total = list.data?.page.total ?? rows.length;

  return (
    <div className="wp-stack">
      <ScreenHeading>{t('nav.reports')}</ScreenHeading>
      <p className="wp-small wp-muted" data-testid="k8-reports-intro">
        {t('k8.reports.intro')}
      </p>

      <CreateReportForm onCreated={onCreated} />

      <section className="wp-section" aria-labelledby="k8-list-heading" data-testid="k8-list">
        <h2 className="wp-section__title" id="k8-list-heading" tabIndex={-1}>
          {t('k8.reports.list.title')}
        </h2>
        <p className="wp-small wp-muted" data-testid="k8-list-companywide">
          {t('k8.reports.list.companyWide')}
        </p>
        <p className="wp-small wp-muted" data-testid="k8-list-reason-note">
          {t('k8.reports.download.reasonNote')}
        </p>

        {list.status === 'error' && list.error !== null ? (
          <ApiErrorNote
            error={list.error}
            onRetry={list.reload}
            subject={t('nav.reports')}
            testId="k8-list-error"
          />
        ) : null}

        {/* A skeleton, not an empty state: "nothing has been read yet" and "this
            company has exported nothing" are two different sentences, and only the
            second is a claim about the company. */}
        {list.status === 'loading' ? (
          <Skeleton label={t('k8.reports.list.title')} testId="k8-list-loading" />
        ) : list.data === null ? null : rows.length === 0 ? (
          <EmptyState
            title={t('k8.reports.list.empty.title')}
            description={t('k8.reports.list.empty.body')}
            testId="k8-list-empty"
          />
        ) : (
          <>
            {rows.length < total ? (
              <PartialData
                excludes={t('k8.reports.list.partial', { shown: rows.length, total })}
                testId="k8-list-partial"
              />
            ) : null}
            <div className="wp-records">
              {rows.map((report) => (
                <ReportRow
                  key={report.id}
                  initial={report}
                  {...(navigate === undefined ? {} : { navigate })}
                />
              ))}
            </div>
          </>
        )}
      </section>

      <AbsenceLegend />

      {companyScope ? (
        <>
          <ScheduleForm onCreated={onScheduleCreated} />
          <ScheduleManager scheduleId={scheduleId} onSelect={setScheduleId} />
        </>
      ) : (
        // No form, no manager, and no greyed copy of either. A sentence, because a
        // silent absence on a page whose plan line says "schedule CRUD" is
        // indistinguishable from a screen that forgot to build it.
        <StatusNote
          severity="info"
          title={t('k8.sched.scopeNote.title')}
          testId="k8-schedule-scope"
        >
          <p>{t('k8.sched.scopeNote.body', { level: user?.scope_level ?? 'self' })}</p>
        </StatusNote>
      )}

      <AbsentEndpoints />
    </div>
  );
}
